import Mention from "@tiptap/extension-mention";
import { VueRenderer } from "@tiptap/vue-3";
import type { SuggestionProps, SuggestionKeyDownProps } from "@tiptap/suggestion";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import MentionList from "../rich-input/MentionList.vue";

export interface VariableItem {
  id: string;
  label: string;
  description: string;
}

export const REPORT_VARIABLES: VariableItem[] = [
  { id: "N", label: "@N", description: "Number of respondents" },
  { id: "question_name", label: "@question_name", description: "Question short name" },
  { id: "question_title", label: "@question_title", description: "Question display title" },
];

export function createVariableMentionExtension(items: VariableItem[] = REPORT_VARIABLES) {
  return Mention.configure({
    HTMLAttributes: { class: "variable-mention" },
    renderText: ({ node }) => `@${node.attrs.id}`,
    renderHTML: ({ node }) => `@${node.attrs.id}`,
    suggestion: {
      char: "@",
      items: ({ query }: { query: string }): VariableItem[] => {
        return items.filter(
          (item) =>
            item.label.toLowerCase().includes(query.toLowerCase()) ||
            item.id.toLowerCase().includes(query.toLowerCase()),
        );
      },
      command: ({ editor, range, props }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: "mention",
            attrs: { id: props.id, label: `@${props.id}` },
          })
          .run();
      },
      render: () => {
        let component: VueRenderer;
        let popup: TippyInstance[];

        return {
          onStart: (props: SuggestionProps<VariableItem>) => {
            component = new VueRenderer(MentionList, { props, editor: props.editor });
            popup = tippy("body", {
              getReferenceClientRect: props.clientRect as () => DOMRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: "manual",
              placement: "bottom-start",
            });
          },
          onUpdate: (props: SuggestionProps<VariableItem>) => {
            component.updateProps(props);
            popup[0].setProps({ getReferenceClientRect: props.clientRect as () => DOMRect });
          },
          onKeyDown: (props: SuggestionKeyDownProps) => {
            if (props.event.key === "Escape") {
              popup[0].hide();
              return true;
            }
            return component.ref?.onKeyDown(props) ?? false;
          },
          onExit: () => {
            popup[0].destroy();
            component.destroy();
          },
        };
      },
    },
  });
}
